import { placeInSlot, type RenderSlot } from "../Layer";
import { BufferAttribute, BufferGeometry, Color, DataTexture, DoubleSide, FloatType, GLSL3, Mesh, RGBAFormat, ShaderMaterial } from "three";
import { applyHugeBounds } from "./hugeBounds";
import { cacheEncode, cacheEncodeGlsl, resolution } from "./screenUniforms";

// Constant screen-width polyline rendering with fat-line styling at a fraction of Line2's cost:
// one uncapped quad per segment, extruded in the vertex shader along that segment's own
// screen-space normal, so the quads tile the path edge to edge and rasterize width x
// on-screen-length once. Extruding per segment (not sharing mitered vertices per point) is what
// keeps the width constant: a clamped miter visibly tapers zig-zagging paths toward every corner,
// whereas the missing wedge on the outside of a sharp corner reads as a mitre-less join. The path
// lives in a float texture indexed by gl_VertexID (four vertices per segment, no vertex
// attributes), so a path costs one RGBA32F texel per point and geometries share one static index buffer.

const TEX_WIDTH = 4096;
const TEX_WIDTH_MASK = TEX_WIDTH - 1;
const TEX_WIDTH_SHIFT = 12;

const VERTEX_SHADER = /* glsl */ `
uniform sampler2D pathTex;
uniform sampler2D colorTex;
uniform float useVertexColor;
uniform int pointCount;
uniform vec2 resolution;
uniform float linewidth;
out vec3 vColor;

vec3 fetchPoint(int i) {
  i = clamp(i, 0, pointCount - 1);
  return texelFetch(pathTex, ivec2(i & ${TEX_WIDTH_MASK}, i >> ${TEX_WIDTH_SHIFT}), 0).xyz;
}

void main() {
  // four vertices per segment: corners 0,1 sit on the segment's first point,
  // corners 2,3 on its second; the low bit picks the side of the line
  int segment = gl_VertexID >> 2;
  int corner = gl_VertexID & 3;
  int end = corner >> 1;
  int i = segment + end;
  float side = ((corner & 1) == 0) ? 1.0 : -1.0;

  ivec2 texel = ivec2(
    clamp(i, 0, pointCount - 1) & ${TEX_WIDTH_MASK},
    clamp(i, 0, pointCount - 1) >> ${TEX_WIDTH_SHIFT}
  );
  vColor = mix(vec3(1.0), texelFetch(colorTex, texel, 0).rgb, useVertexColor);

  mat4 mvp = projectionMatrix * modelViewMatrix;
  vec4 clipA = mvp * vec4(fetchPoint(segment), 1.0);
  vec4 clipB = mvp * vec4(fetchPoint(segment + 1), 1.0);

  vec2 half_res = 0.5 * resolution;
  vec2 sA = clipA.xy / clipA.w * half_res;
  vec2 sB = clipB.xy / clipB.w * half_res;

  vec2 delta = sB - sA;
  float len = length(delta);
  vec2 dir = len > 1e-6 ? delta / len : vec2(1.0, 0.0);
  vec2 normal = vec2(-dir.y, dir.x);

  vec4 clip = (end == 0) ? clipA : clipB;
  clip.xy += normal * (side * 0.5 * linewidth) / half_res * clip.w;
  gl_Position = clip;
}
`;

const FRAGMENT_SHADER = /* glsl */ `
uniform vec3 color;
uniform float opacity;
uniform float cacheEncode;
in vec3 vColor;
out vec4 outColor;

void main() {
  vec4 c = vec4(color * vColor, opacity);
  outColor = ${cacheEncodeGlsl("c")};
}
`;

// One static index buffer shared by all ribbon geometries: triangles
// (4s, 4s+1, 4s+2) / (4s+1, 4s+3, 4s+2) turn each segment's four corners into
// a quad. Grown geometrically when a longer path appears.
let sharedIndex = new BufferAttribute(new Uint32Array(0), 1);

function ensureSharedIndex(pointCount: number): BufferAttribute {
  const needed = Math.max(0, pointCount - 1) * 6;
  if (sharedIndex.count >= needed) return sharedIndex;
  const capacity = Math.max(needed, sharedIndex.count * 2, 6 * 4096);
  const segments = Math.ceil(capacity / 6);
  const indices = new Uint32Array(segments * 6);
  for (let s = 0; s < segments; s++) {
    const v = 4 * s;
    const o = 6 * s;
    indices[o] = v;
    indices[o + 1] = v + 1;
    indices[o + 2] = v + 2;
    indices[o + 3] = v + 1;
    indices[o + 4] = v + 3;
    indices[o + 5] = v + 2;
  }
  sharedIndex = new BufferAttribute(indices, 1);
  return sharedIndex;
}

type PathRibbonStyle = {
  color: string;
  opacity: number;
  linewidth: number;
  slot: RenderSlot;
};

const WHITE = new Color(1, 1, 1);

// A TEX_WIDTH x rows float RGBA texture, reused in place when large enough
// (grow-only): solver steps replace paths dozens of times per second, and
// allocating a texture per step churns both the GC and the GL driver.
function ensureTexture(texture: DataTexture | null, rows: number): DataTexture {
  if (texture && texture.image.height >= rows) return texture;
  texture?.dispose();
  // a DataTexture samples nearest and never mipmaps by default, as a lookup table should
  return new DataTexture(new Float32Array(TEX_WIDTH * rows * 4), TEX_WIDTH, rows, RGBAFormat, FloatType);
}

// `count` xyz triples uploaded as the rgba rows of a texture at least `rows` tall (alpha 1); stale
// texels beyond `count` are never fetched (indices clamp)
function uploadPointTexture(texture: DataTexture | null, rows: number, source: Float32Array, count: number): DataTexture {
  const ensured = ensureTexture(texture, rows);
  const data = ensured.image.data as Float32Array;
  for (let i = 0; i < count; i++) {
    data[i * 4] = source[i * 3]!;
    data[i * 4 + 1] = source[i * 3 + 1]!;
    data[i * 4 + 2] = source[i * 3 + 2]!;
    data[i * 4 + 3] = 1;
  }
  ensured.needsUpdate = true;
  return ensured;
}

// bound when a ribbon has no per-point colors, keeping a single program
const dummyColorTexture = new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, RGBAFormat);
dummyColorTexture.needsUpdate = true;

export class PathRibbon {
  readonly object3D: Mesh;
  private material: ShaderMaterial;
  private geometry: BufferGeometry;
  private texture: DataTexture | null = null;
  private colorTexture: DataTexture | null = null;
  private baseColor: Color;

  constructor(style: PathRibbonStyle) {
    // linear working-space color, like the built-in materials; output
    // encoding happens in the fragment shader via linearToOutputTexel
    const color = new Color(style.color);
    this.baseColor = color.clone();
    this.material = new ShaderMaterial({
      glslVersion: GLSL3,
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      uniforms: {
        pathTex: { value: null },
        colorTex: { value: dummyColorTexture },
        useVertexColor: { value: 0 },
        pointCount: { value: 0 },
        resolution: { value: resolution },
        cacheEncode,
        linewidth: { value: style.linewidth },
        color: { value: color },
        opacity: { value: style.opacity },
      },
      transparent: style.opacity < 1,
      depthTest: false,
      depthWrite: false,
      side: DoubleSide,
    });
    this.geometry = new BufferGeometry();
    applyHugeBounds(this.geometry);
    this.object3D = placeInSlot(new Mesh(this.geometry, this.material), style.slot);
  }

  setDepth(enabled: boolean): void {
    this.material.depthTest = enabled;
    this.material.depthWrite = enabled;
  }

  // points: per-point [x, y, z]; colors: optional per-point linear RGB.
  setPath(points: Float32Array, pointCount: number, colors?: Float32Array | null): void {
    const rows = Math.max(1, Math.ceil(pointCount / TEX_WIDTH));
    this.texture = uploadPointTexture(this.texture, rows, points, pointCount);
    this.material.uniforms.pathTex!.value = this.texture;
    this.material.uniforms.pointCount!.value = pointCount;

    // with per-point colors the uniform must not tint them
    (this.material.uniforms.color!.value as Color).copy(colors ? WHITE : this.baseColor);
    if (colors) {
      this.colorTexture = uploadPointTexture(this.colorTexture, rows, colors, pointCount);
      this.material.uniforms.colorTex!.value = this.colorTexture;
      this.material.uniforms.useVertexColor!.value = 1;
    } else {
      this.material.uniforms.colorTex!.value = dummyColorTexture;
      this.material.uniforms.useVertexColor!.value = 0;
    }

    this.geometry.setIndex(ensureSharedIndex(pointCount));
    this.geometry.setDrawRange(0, Math.max(0, pointCount - 1) * 6);
  }

  dispose(): void {
    this.texture?.dispose();
    this.colorTexture?.dispose();
    this.material.dispose();
    this.geometry.dispose();
  }
}
